import jsPDF from 'jspdf';
import ExcelJS from 'exceljs';
import html2canvas from 'html2canvas';
import { SSF } from 'xlsx';

/**
 * Converts an Excel invoice (Blob) to a faithful PDF rendering.
 *
 * The generated invoice Excel is the single source of truth, so this renders the
 * worksheet exactly as laid out: merged cells, the embedded logo image, column
 * widths / row heights, fills, borders, fonts, and Excel number formats. The
 * rendered HTML is rasterized with html2canvas and placed on an A4 page.
 *
 * @param excelBlob The Excel file as a Blob
 * @returns A Blob containing the PDF
 */
export const convertExcelToPDF = async (excelBlob: Blob): Promise<Blob> => {
  let container: HTMLDivElement | null = null;
  try {
    const workbook = new ExcelJS.Workbook();
    const arrayBuffer = await excelBlob.arrayBuffer();
    await workbook.xlsx.load(arrayBuffer);

    const worksheet = workbook.worksheets[0];
    if (!worksheet) {
      throw new Error('No worksheet found in Excel file');
    }

    const rowCount = worksheet.rowCount;
    const colCount = worksheet.columnCount;

    // Precompute pixel dimensions for every column and row so merged cells and
    // the logo can be sized deterministically.
    const colWidthsPx: number[] = [0];
    for (let c = 1; c <= colCount; c++) {
      colWidthsPx[c] = colWidthToPx(worksheet.getColumn(c).width);
    }
    const rowHeightsPx: number[] = [0];
    for (let r = 1; r <= rowCount; r++) {
      rowHeightsPx[r] = rowHeightToPx(worksheet.getRow(r).height);
    }
    const sumColPx = (from: number, to: number) => {
      let total = 0;
      for (let c = from; c <= to; c++) total += colWidthsPx[c] || 0;
      return total;
    };
    const sumRowPx = (from: number, to: number) => {
      let total = 0;
      for (let r = from; r <= to; r++) total += rowHeightsPx[r] || 0;
      return total;
    };

    // Map merged regions: master (top-left) cells get colspan/rowspan, the rest
    // are "covered" and skipped during rendering.
    const masterSpans = new Map<string, { colspan: number; rowspan: number }>();
    const coveredCells = new Set<string>();
    const merges: string[] = (worksheet.model as any).merges || [];
    for (const merge of merges) {
      const [start, end] = merge.split(':');
      const a = parseAddress(start);
      const b = parseAddress(end);
      const minR = Math.min(a.row, b.row);
      const maxR = Math.max(a.row, b.row);
      const minC = Math.min(a.col, b.col);
      const maxC = Math.max(a.col, b.col);
      masterSpans.set(`${minR}_${minC}`, { colspan: maxC - minC + 1, rowspan: maxR - minR + 1 });
      for (let r = minR; r <= maxR; r++) {
        for (let c = minC; c <= maxC; c++) {
          if (r === minR && c === minC) continue;
          coveredCells.add(`${r}_${c}`);
        }
      }
    }

    // Map embedded images by their top-left anchor cell.
    const imageMap = new Map<string, string>();
    for (const image of worksheet.getImages()) {
      const media = (workbook.model as any).media?.find((m: any) => m.index === image.imageId);
      if (!media || !media.buffer) continue;
      const u8 = media.buffer instanceof Uint8Array ? media.buffer : new Uint8Array(media.buffer);
      const mime = media.extension === 'jpg' || media.extension === 'jpeg'
        ? 'image/jpeg'
        : `image/${media.extension || 'png'}`;
      const tl: any = image.range.tl;
      const key = `${tl.nativeRow + 1}_${tl.nativeCol + 1}`;
      imageMap.set(key, `data:${mime};base64,${uint8ToBase64(u8)}`);
    }

    // Build an off-screen HTML table mirroring the worksheet.
    const totalWidthPx = sumColPx(1, colCount);
    container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '0';
    container.style.background = 'white';
    container.style.width = `${totalWidthPx}px`;

    const table = document.createElement('table');
    table.style.borderCollapse = 'collapse';
    table.style.tableLayout = 'fixed';
    table.style.width = `${totalWidthPx}px`;
    table.style.fontFamily = 'Arial, sans-serif';

    const colgroup = document.createElement('colgroup');
    for (let c = 1; c <= colCount; c++) {
      const col = document.createElement('col');
      col.style.width = `${colWidthsPx[c]}px`;
      colgroup.appendChild(col);
    }
    table.appendChild(colgroup);

    const tbody = document.createElement('tbody');
    const imageLoads: Promise<unknown>[] = [];

    for (let r = 1; r <= rowCount; r++) {
      const tr = document.createElement('tr');
      tr.style.height = `${rowHeightsPx[r]}px`;

      for (let c = 1; c <= colCount; c++) {
        const key = `${r}_${c}`;
        if (coveredCells.has(key)) continue;

        const cell = worksheet.getCell(r, c);
        const span = masterSpans.get(key);
        const td = document.createElement('td');

        if (span) {
          if (span.colspan > 1) td.colSpan = span.colspan;
          if (span.rowspan > 1) td.rowSpan = span.rowspan;
        }

        td.style.boxSizing = 'border-box';
        td.style.padding = '1px 4px';
        // Note: no overflow:hidden — Excel lets text spill into adjacent empty
        // cells, so we allow the same instead of clipping at the column edge.

        applyBorders(td, cell.border);
        applyFont(td, cell.font);
        applyFill(td, cell.fill);
        applyAlignment(td, cell.alignment, span);

        const imageData = imageMap.get(key);
        if (imageData) {
          const img = document.createElement('img');
          img.src = imageData;
          const spanW = span ? sumColPx(c, c + span.colspan - 1) : colWidthsPx[c];
          const spanH = span ? sumRowPx(r, r + span.rowspan - 1) : rowHeightsPx[r];
          img.style.maxWidth = `${spanW}px`;
          img.style.maxHeight = `${spanH}px`;
          img.style.width = 'auto';
          img.style.height = 'auto';
          img.style.display = 'block';
          td.style.textAlign = 'center';
          imageLoads.push(new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
          }));
          td.appendChild(img);
        } else {
          td.textContent = formatCellValue(cell);
        }

        tr.appendChild(td);
      }

      tbody.appendChild(tr);
    }

    table.appendChild(tbody);
    container.appendChild(table);
    document.body.appendChild(container);

    // Wait for the logo (and any other images) to load before rasterizing.
    await Promise.all(imageLoads);

    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false
    });

    // Fit the rendered invoice onto a single A4 portrait page with a small margin.
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 8;
    const usableWidth = pageWidth - margin * 2;
    const usableHeight = pageHeight - margin * 2;
    const ratio = Math.min(usableWidth / canvas.width, usableHeight / canvas.height);
    const imgWidth = canvas.width * ratio;
    const imgHeight = canvas.height * ratio;
    const x = (pageWidth - imgWidth) / 2;
    const y = margin;

    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', x, y, imgWidth, imgHeight);

    return pdf.output('blob');
  } catch (error) {
    console.error('Error converting Excel to PDF:', error);
    throw new Error(`Failed to convert Excel to PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
  } finally {
    if (container && container.parentNode) {
      container.parentNode.removeChild(container);
    }
  }
};

// --- Helpers ---------------------------------------------------------------

// Excel column width is measured in characters; approximate the MS pixel formula.
const colWidthToPx = (width?: number): number => Math.round((width ?? 11) * 7 + 5);

// Excel row height is measured in points; convert to pixels at 96 DPI.
const rowHeightToPx = (height?: number): number => Math.round((height ?? 15) * (96 / 72));

const colLetterToNum = (letters: string): number => {
  let n = 0;
  for (let i = 0; i < letters.length; i++) {
    n = n * 26 + (letters.charCodeAt(i) - 64);
  }
  return n;
};

const parseAddress = (address: string): { row: number; col: number } => {
  const match = /^([A-Z]+)(\d+)$/.exec(address);
  if (!match) return { row: 1, col: 1 };
  return { col: colLetterToNum(match[1]), row: parseInt(match[2], 10) };
};

// Default Office theme colors, indexed the way style `theme` attributes reference
// them (0 = background1/white, 1 = text1/black, 2 = background2, ...).
const THEME_COLORS = [
  'FFFFFF', '000000', 'E7E6E6', '44546A', '4472C4', 'ED7D31',
  'A5A5A5', 'FFC000', '5B9BD5', '70AD47', '0563C1', '954F72'
];

// Common entries from the legacy indexed color palette.
const INDEXED_COLORS: Record<number, string> = {
  8: '000000', 9: 'FFFFFF', 10: 'FF0000', 11: '00FF00', 12: '0000FF', 13: 'FFFF00',
  14: 'FF00FF', 15: '00FFFF', 16: '800000', 17: '008000', 18: '000080', 19: '808000',
  20: '800080', 21: '008080', 22: 'C0C0C0', 23: '808080', 55: '969696', 63: '333333'
};

// Apply an Excel tint to a hex color (negative darkens, positive lightens).
const applyTint = (hex: string, tint?: number): string => {
  if (!tint) return hex;
  const num = parseInt(hex, 16);
  const adjust = (channel: number) => {
    const value = tint < 0 ? channel * (1 + tint) : channel + (255 - channel) * tint;
    return Math.max(0, Math.min(255, Math.round(value)));
  };
  const r = adjust((num >> 16) & 0xff);
  const g = adjust((num >> 8) & 0xff);
  const b = adjust(num & 0xff);
  return ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
};

// Resolve an ExcelJS color (argb, theme+tint, or indexed) to a CSS hex string.
const resolveColor = (color?: any): string | undefined => {
  if (!color) return undefined;
  if (typeof color.argb === 'string') {
    // ExcelJS uses AARRGGBB; drop the alpha channel for CSS.
    return color.argb.length === 8 ? `#${color.argb.substring(2)}` : `#${color.argb}`;
  }
  if (typeof color.theme === 'number') {
    const base = THEME_COLORS[color.theme];
    return base ? `#${applyTint(base, color.tint)}` : undefined;
  }
  if (typeof color.indexed === 'number') {
    const hex = INDEXED_COLORS[color.indexed];
    return hex ? `#${hex}` : undefined;
  }
  return undefined;
};

const dateToSerial = (date: Date): number =>
  25569 + (date.getTime() - date.getTimezoneOffset() * 60000) / 86400000;

const formatNumberOrDate = (value: number | Date, numFmt?: string): string => {
  try {
    if (value instanceof Date) {
      return SSF.format(numFmt || 'm/d/yyyy', dateToSerial(value));
    }
    return SSF.format(numFmt || 'General', value);
  } catch {
    return String(value);
  }
};

const formatCellValue = (cell: ExcelJS.Cell): string => {
  const value: any = cell.value;
  if (value === null || value === undefined) return '';

  if (typeof value === 'object') {
    if ('richText' in value && Array.isArray(value.richText)) {
      return value.richText.map((rt: any) => rt.text).join('');
    }
    if ('formula' in value) {
      const result = value.result;
      if (result === null || result === undefined) return '';
      if (typeof result === 'number' || result instanceof Date) {
        return formatNumberOrDate(result, cell.numFmt);
      }
      return String(result);
    }
    if (value instanceof Date) {
      return formatNumberOrDate(value, cell.numFmt);
    }
    if ('text' in value) return String(value.text);
    if ('hyperlink' in value) return String(value.hyperlink);
    return '';
  }

  if (typeof value === 'number') {
    return formatNumberOrDate(value, cell.numFmt);
  }
  return String(value);
};

const borderEdgeToCss = (edge?: Partial<ExcelJS.Border>): string => {
  if (!edge || !edge.style) return '';
  const color = resolveColor(edge.color) || '#000000';
  const width = edge.style === 'medium' || edge.style === 'thick' ? '2px' : '1px';
  const lineStyle = edge.style === 'dotted' ? 'dotted' : edge.style === 'dashed' ? 'dashed' : 'solid';
  return `${width} ${lineStyle} ${color}`;
};

const applyBorders = (td: HTMLTableCellElement, border?: Partial<ExcelJS.Borders>): void => {
  if (!border) return;
  const top = borderEdgeToCss(border.top);
  const right = borderEdgeToCss(border.right);
  const bottom = borderEdgeToCss(border.bottom);
  const left = borderEdgeToCss(border.left);
  if (top) td.style.borderTop = top;
  if (right) td.style.borderRight = right;
  if (bottom) td.style.borderBottom = bottom;
  if (left) td.style.borderLeft = left;
};

const applyFont = (td: HTMLTableCellElement, font?: Partial<ExcelJS.Font>): void => {
  const size = font?.size ?? 11;
  td.style.fontSize = `${(size * 96 / 72).toFixed(1)}px`;
  td.style.fontFamily = font?.name ? `'${font.name}', Arial, sans-serif` : 'Arial, sans-serif';
  if (font?.bold) td.style.fontWeight = 'bold';
  if (font?.italic) td.style.fontStyle = 'italic';
  const color = resolveColor(font?.color);
  if (color) td.style.color = color;
};

const applyFill = (td: HTMLTableCellElement, fill?: ExcelJS.Fill): void => {
  if (!fill || fill.type !== 'pattern' || fill.pattern === 'none') return;
  const bg = resolveColor((fill as ExcelJS.FillPattern).fgColor);
  if (bg) td.style.backgroundColor = bg;
};

const applyAlignment = (
  td: HTMLTableCellElement,
  alignment?: Partial<ExcelJS.Alignment>,
  span?: { colspan: number; rowspan: number }
): void => {
  td.style.verticalAlign = alignment?.vertical || 'bottom';
  if (alignment?.horizontal && alignment.horizontal !== 'fill') {
    td.style.textAlign = alignment.horizontal;
  }
  // Wrap text in cells that ask for it, or in tall merged cells (e.g. comments).
  // Otherwise use `pre` (not `nowrap`) so internal spacing — which Excel uses to
  // align header labels like "BOL   Miles   Description" — is preserved.
  td.style.whiteSpace = alignment?.wrapText || (span && span.rowspan > 1) ? 'pre-wrap' : 'pre';
};

const uint8ToBase64 = (bytes: Uint8Array): string => {
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunkSize)));
  }
  return btoa(binary);
};
